import React, { useEffect, useState } from "react";

/**
 * Floating Table of Contents — desktop-only sticky sidebar with scroll spy.
 * Smooth-scrolls to each section, highlights the currently visible H2.
 *
 * @param {Array<{id: string, label: string}>} items
 * @param {string} title - heading shown above the list
 */
export const TableOfContents = ({ items, title = "Sommaire" }) => {
  const [active, setActive] = useState(items[0]?.id || "");

  useEffect(() => {
    const observers = [];
    const onIntersect = (entries) => {
      const visible = entries
        .filter((e) => e.isIntersecting)
        .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
      if (visible) setActive(visible.target.id);
    };
    const obs = new IntersectionObserver(onIntersect, {
      rootMargin: "-25% 0px -55% 0px",
      threshold: [0, 1],
    });
    items.forEach((it) => {
      const el = document.getElementById(it.id);
      if (el) {
        obs.observe(el);
        observers.push(el);
      }
    });
    return () => obs.disconnect();
  }, [items]);

  const handleClick = (e, id) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      window.history.replaceState({}, "", `#${id}`);
    }
  };

  return (
    <nav
      className="hidden xl:block sticky top-28 self-start w-64 shrink-0 ml-8"
      aria-label={title}
      data-testid="floating-toc"
    >
      <div className="rounded-2xl border border-[#D4AF37]/25 bg-gradient-to-b from-[#0A1128]/70 to-[#0F1A3A]/40 backdrop-blur-md p-5">
        <p className="label-overline mb-4 text-[#D4AF37]">{title}</p>
        <ul className="space-y-1">
          {items.map((it) => {
            const isActive = active === it.id;
            return (
              <li key={it.id}>
                <a
                  href={`#${it.id}`}
                  onClick={(e) => handleClick(e, it.id)}
                  data-testid={`toc-${it.id}`}
                  className={`group flex items-start gap-3 py-2 pl-3 pr-2 text-sm leading-snug transition-colors border-l-2 ${
                    isActive
                      ? "border-[#00F0FF] text-[#00F0FF]"
                      : "border-[#1E293B] text-slate-400 hover:text-slate-200 hover:border-[#00F0FF]/40"
                  }`}
                >
                  <span className="font-mono-tech text-[10px] uppercase tracking-[0.2em] mt-1 opacity-70">
                    {String(items.indexOf(it) + 1).padStart(2, "0")}
                  </span>
                  <span className="font-sans">{it.label}</span>
                </a>
              </li>
            );
          })}
        </ul>
      </div>
    </nav>
  );
};
